import { apiClient } from "@/lib/api/client";
import { assign, fromPromise, setup } from "xstate";

export type SelectedEquipmentOption = {
  id: string; // Equipment.id
  value: string; // Equipment.id
  label: string; // Equipment.name
  description?: string; // Equipment.remarks
  disabled?: boolean;
};

type SearchEquipmentsOutput = SelectedEquipmentOption[];
type SearchEquipmentsInput = {
  searchText: string;
  equipmentType: "all" | "providable";
};

type GetEquipmentOutput = SelectedEquipmentOption;
type GetEquipmentInput = { equipmentId: string };

const searchEquipmentsActor = fromPromise<
  SearchEquipmentsOutput,
  SearchEquipmentsInput
>(async ({ input }) => {
  try {
    const res = await apiClient.GET("/v2/components/equipment-select", {
      params: {
        query: {
          freeText: input.searchText,
          onlyProvidable: input.equipmentType === "providable",
          pageSize: 5,
        },
      },
    });
    if (!res.data?.items) return [];
    return res.data.items.map((equipment) => ({
      id: equipment.id,
      value: equipment.id,
      label: equipment.name,
      description: equipment.equipmentTypeName,
    }));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "";
    throw new Error(errorMessage);
  }
});

const getEquipmentActor = fromPromise<GetEquipmentOutput, GetEquipmentInput>(
  async ({ input }) => {
    try {
      const res = await apiClient.GET("/v2/components/equipment-select", {
        params: { query: { idIn: [input.equipmentId] } },
      });

      const equipment = res.data?.items[0];
      if (!equipment) throw new Error("Equipment not found");
      return {
        id: equipment.id,
        value: equipment.id,
        label: equipment.name,
        description: equipment.equipmentTypeName,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      throw new Error(errorMessage);
    }
  },
);

type Context = {
  selectedEquipmentId: string;
  selectedEquipmentOption: SelectedEquipmentOption | null;
  searchText: string;
  searchResults: SelectedEquipmentOption[];
  errorMessage: string | null;
};

type Event =
  | { type: "INIT" }
  | { type: "GET_EQUIPMENT"; equipmentId: string }
  | { type: "SEARCH_EQUIPMENT"; text: string }
  | {
      type: "SELECT_EQUIPMENT";
      equipmentId: string;
      equipmentOption: SelectedEquipmentOption;
    }
  | { type: "REMOVE_EQUIPMENT"; equipmentId: string };

type Input = {
  selectedEquipmentId?: string;
};

const initialContext: Context = {
  selectedEquipmentId: "",
  selectedEquipmentOption: null,
  searchText: "",
  searchResults: [],
  errorMessage: null,
};

export const createEquipmentSelectMachine = (
  equipmentType: "all" | "providable",
) => {
  return setup({
    types: {
      context: {} as Context,
      events: {} as Event,
      input: {} as Input,
    },
    actors: {
      searchEquipments: searchEquipmentsActor,
      getEquipment: getEquipmentActor,
    },
    actions: {},
    guards: {
      isInitializedWithEquipmentId: ({ context }) =>
        !!context.selectedEquipmentId &&
        context.selectedEquipmentOption == null,
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5SzAQwE4GMAWAFM6sA9gHYB0AliRQC4DEAkgHIMAqA2gAwC6ioADkVi0KpPiAAeiAIwAmAKxkAnNM7yls2QA5ZnLRoA0IAJ6JZAZgBsZeZ0sB2VbIAs0+5dmWtAX29GUGDj4hKSU1PTMbOzSvEgggsI0oiTiUghyiqruGtq6+rJGpgha0mTSJVrOSu5eskryzr7+aFh4BMTkKAA2YJhJJFDBHXQAygCiAIIASgDCABIA+rhjUyMA8kxcsQJCImJxaZbyiqruVp7meuqFiOZa5mT38vZ3deae5Y1+IAGtQ6HdXr9QbtUijMYAGTGM1YSxW602PHEST2KQOiC0RxsWnuei07mcznuNwQsjcZHc0ks9Vs7nk0juTR+LSCoM6YB6fSoIJCJDoUzGAFk1gA1MZw1YbLbI3ZJfagNKY2RlezPczSZyaSycZz2ElyB62Tx2ZyWcxEqxKJm-Vm8sgwGj-PkQUhgMIANyIAGs3Ta2naHU6EFRPZhUHKSFtpXEURHUohLESyJxpBkGZxzOoySTdKVLvY1BqSpYvF5rSz-R17WBHWy6AR0ER0GR+F1wwAzJsAWzIfqd1drvODJFD4eSUaRMdlyXjCETWmTqYU6czGmkOY0Nkc5iURPxpvknnLgUrAIr3KddBdJDdIe9vor-b9F7Zw9HEYn23i0-lkgTSZTNNLlXbMTEQXUF04XcdBecx7E1eovmaE8n3PAZLwbJsWzbGhO3QHs+zZXs0J5Do3yIMMPx4aMdkSGd0TnACl3pYCs3XMCEHkakyBAnV3jkVV5F8b4SCICA4HEQjeRlOjfzSABaSwSUU48-iIqhaBk1FZ01El8TIXV7BUeCcWkDRd1U20q0BLl0LZLS4wY-FOBsBQrHMTMoNsZwSXVexk10exKmOcozOkSzT3IQN7KnWS0QVcDLgpVMjmkBpMy8JQcwZZN5DuPQC1NBCItQk8X2k2LtIY1wXPUNKSg85xznYopnE4ZUNA8MkaTazwvl8IA */
    id: "searchEquipment",
    initial: "init",
    context: ({ input }) => ({
      ...initialContext,
      selectedEquipmentId: input.selectedEquipmentId ?? "",
    }),
    states: {
      init: {
        on: {
          INIT: [
            {
              guard: "isInitializedWithEquipmentId",
              target: "getEquipment",
            },
            {
              target: "selectingEquipment",
            },
          ],
        },
      },
      selectingEquipment: {
        on: {
          SEARCH_EQUIPMENT: {
            actions: assign({
              searchText: ({ event }) => event.text,
            }),
            target: "searchingEquipment",
          },
          SELECT_EQUIPMENT: {
            actions: assign({
              selectedEquipmentId: ({ event }) => event.equipmentId,
              selectedEquipmentOption: ({ event }) => event.equipmentOption,
              searchText: "",
              searchResults: [],
              errorMessage: null,
            }),
          },
          REMOVE_EQUIPMENT: {
            actions: assign({
              selectedEquipmentId: () => "",
              selectedEquipmentOption: null,
              errorMessage: null,
            }),
          },
        },
      },
      getEquipment: {
        invoke: {
          src: "getEquipment",
          input: ({ context }) => ({
            equipmentId: context.selectedEquipmentId,
          }),
          onDone: [
            {
              target: "selectingEquipment",
              actions: assign({
                selectedEquipmentOption: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingEquipment",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
      searchingEquipment: {
        invoke: {
          src: "searchEquipments",
          input: ({ context }) => ({
            searchText: context.searchText,
            equipmentType,
          }),
          onDone: [
            {
              target: "selectingEquipment",
              actions: assign({
                searchResults: ({ event }) => event.output,
              }),
            },
          ],
          onError: [
            {
              target: "selectingEquipment",
              actions: assign({
                errorMessage: ({ event }) => {
                  const error = event.error as Error;
                  return error?.message || "";
                },
                searchResults: [],
              }),
            },
          ],
        },
      },
    },
  });
};
